package fr.manyverse.app.presentation.components.soundboard

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Shuffle
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupProperties
import fr.manyverse.app.domain.model.SoundTag
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.auto_play_active
import manyverse.shared.generated.resources.sound_shuffle
import manyverse.shared.generated.resources.sound_stop_playback
import manyverse.shared.generated.resources.tag_calm
import manyverse.shared.generated.resources.tag_combat
import manyverse.shared.generated.resources.tag_tension
import manyverse.shared.generated.resources.tap_to_change_music
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.roundToInt
import kotlin.math.sin

private const val SELECTION_RADIUS_DP = 55f
private const val OPTION_SIZE_DP = 64f

/**
 * Radial music selector that appears on tap.
 * Shows 3 options (CALM, TENSION, COMBAT) positioned at 120° intervals.
 * User taps to open, drags toward an option, and releases to select.
 */
@Composable
fun RadialMusicSelector(
    onTagSelected: (SoundTag) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    activeTag: SoundTag? = null,
    onDeactivate: () -> Unit = {}
) {
    val density = LocalDensity.current

    // State
    var isExpanded by remember { mutableStateOf(false) }
    var selectedOption by remember { mutableStateOf<SoundTag?>(null) }
    var isDeactivateHovered by remember { mutableStateOf(false) }

    // Strings for accessibility
    val contentDesc = if (activeTag != null)
        stringResource(Res.string.auto_play_active)
    else
        stringResource(Res.string.tap_to_change_music)

    // Options configuration: angle in degrees (0 = right, 90 = down, etc.)
    // All options positioned below the button for visibility
    val options = remember {
        listOf(
            SoundTag.CALM to 90f,     // Bottom center
            SoundTag.COMBAT to 10f,   // Bottom-right
            SoundTag.TENSION to 170f  // Bottom-left
        )
    }

    // Calculate selection radius and min distance in pixels
    val selectionRadiusPx = with(density) { SELECTION_RADIUS_DP.dp.toPx() }

    // Animation for expansion
    val expansionProgress by animateFloatAsState(
        targetValue = if (isExpanded) 1f else 0f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessMedium
        ),
        label = "expansion"
    )

    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center
    ) {
        // Central button
        Surface(
            shape = CircleShape,
            color = if (activeTag != null)
                MaterialTheme.colorScheme.primary
            else
                MaterialTheme.colorScheme.surfaceVariant,
            tonalElevation = if (activeTag != null) 8.dp else 4.dp,
            modifier = Modifier
                .size(40.dp)
                .semantics { contentDescription = contentDesc }
                .pointerInput(enabled) {
                    if (!enabled) return@pointerInput

                    detectTapGestures(
                        onTap = {
                            // Toggle menu on tap
                            selectedOption = null
                            isExpanded = !isExpanded
                        }
                    )
                }
        ) {
            Box(
                modifier = Modifier.size(40.dp).background(
                    color = if (activeTag != null)
                        MaterialTheme.colorScheme.primary
                    else
                        MaterialTheme.colorScheme.surfaceVariant
                ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.Shuffle,
                    contentDescription = stringResource(Res.string.sound_shuffle),
                    tint = if (activeTag != null)
                        MaterialTheme.colorScheme.onPrimary
                    else
                        MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.size(20.dp)
                )
            }
        }

        // Fullscreen dismiss overlay (shown when expanded)
        if (isExpanded) {
            // Overlay to catch clicks outside and dismiss
            Popup(
                alignment = Alignment.TopStart,
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = false
                ),
                onDismissRequest = {
                    isExpanded = false
                    selectedOption = null
                    isDeactivateHovered = false
                }
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .pointerInput(Unit) {
                            detectTapGestures(
                                onTap = {
                                    isExpanded = false
                                    selectedOption = null
                                    isDeactivateHovered = false
                                }
                            )
                        }
                )
            }

            // Options popup (positioned relative to button)
            Popup(
                alignment = Alignment.Center,
                properties = PopupProperties(
                    focusable = false,
                    dismissOnBackPress = false,
                    dismissOnClickOutside = false
                )
            ) {
                Box(
                    modifier = Modifier.size((SELECTION_RADIUS_DP * 2 + OPTION_SIZE_DP).dp),
                    contentAlignment = Alignment.Center
                ) {
                    options.forEach { (tag, angle) ->
                        RadialOption(
                            tag = tag,
                            angle = angle,
                            distance = selectionRadiusPx * expansionProgress,
                            isHovered = selectedOption == tag,
                            isActive = tag == activeTag,
                            alpha = expansionProgress,
                            onHover = { hovered ->
                                selectedOption = if (hovered) tag else null
                                if (hovered) isDeactivateHovered = false
                            },
                            onClick = {
                                onTagSelected(tag)
                                isExpanded = false
                                selectedOption = null
                            }
                        )
                    }

                    // Deactivate option (top, 270°) — only visible when auto-play is active
                    if (activeTag != null) {
                        DeactivateRadialOption(
                            angle = 270f,
                            distance = selectionRadiusPx * expansionProgress,
                            isHovered = isDeactivateHovered,
                            alpha = expansionProgress,
                            onHover = { hovered ->
                                isDeactivateHovered = hovered
                                if (hovered) selectedOption = null
                            },
                            onClick = {
                                onDeactivate()
                                isExpanded = false
                                isDeactivateHovered = false
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun DeactivateRadialOption(
    angle: Float,
    distance: Float,
    isHovered: Boolean,
    alpha: Float,
    onHover: (Boolean) -> Unit,
    onClick: () -> Unit
) {
    val angleRad = angle * (PI.toFloat() / 180f)
    val offsetX = cos(angleRad) * distance
    val offsetY = sin(angleRad) * distance

    val interactionSource = remember { MutableInteractionSource() }
    val isHoveredLocal by interactionSource.collectIsHoveredAsState()

    LaunchedEffect(isHoveredLocal) {
        onHover(isHoveredLocal)
    }

    val showHovered = isHovered || isHoveredLocal

    val animatedScale by animateFloatAsState(
        targetValue = if (showHovered) 1.2f else 1f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessHigh
        ),
        label = "deactivateScale"
    )

    Surface(
        shape = CircleShape,
        color = if (showHovered)
            MaterialTheme.colorScheme.primaryContainer
        else
            MaterialTheme.colorScheme.secondaryContainer.copy(alpha = alpha),
        tonalElevation = if (showHovered) 8.dp else 2.dp,
        modifier = Modifier
            .offset {
                IntOffset(
                    x = offsetX.roundToInt(),
                    y = offsetY.roundToInt()
                )
            }
            .scale(animatedScale)
            .size(OPTION_SIZE_DP.dp)
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = alpha),
                shape = CircleShape
            )
            .hoverable(interactionSource = interactionSource)
            .pointerInput(Unit) {
                detectTapGestures(onTap = { onClick() })
            }
    ) {
        Box(
            modifier = Modifier
                .size(OPTION_SIZE_DP.dp)
                .background(
                    color = if (showHovered)
                        MaterialTheme.colorScheme.errorContainer
                    else
                        MaterialTheme.colorScheme.secondaryContainer.copy(alpha = alpha),
                    shape = CircleShape
                ),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = Icons.Default.Close,
                contentDescription = stringResource(Res.string.sound_stop_playback),
                tint = if (showHovered)
                    MaterialTheme.colorScheme.onPrimaryContainer
                else
                    MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = alpha),
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

@Composable
private fun RadialOption(
    tag: SoundTag,
    angle: Float,
    distance: Float,
    isHovered: Boolean,
    isActive: Boolean,
    alpha: Float,
    onHover: (Boolean) -> Unit,
    onClick: () -> Unit
) {
    // Calculate position
    val angleRad = angle * (PI.toFloat() / 180f)
    val offsetX = cos(angleRad) * distance
    val offsetY = sin(angleRad) * distance

    // Get label
    val label = when (tag) {
        SoundTag.CALM -> stringResource(Res.string.tag_calm)
        SoundTag.COMBAT -> stringResource(Res.string.tag_combat)
        SoundTag.TENSION -> stringResource(Res.string.tag_tension)
    }

    // Hover state
    val interactionSource = remember { MutableInteractionSource() }
    val isHoveredLocal by interactionSource.collectIsHoveredAsState()

    // Notify parent of hover state changes
    if (isHoveredLocal) {
        onHover(true)
    }

    // Use either local hover or parent-provided hover state
    val showHovered = isHovered || isHoveredLocal

    // Animate scale based on hover
    val animatedScale by animateFloatAsState(
        targetValue = if (showHovered) 1.2f else 1f,
        animationSpec = spring(
            dampingRatio = Spring.DampingRatioMediumBouncy,
            stiffness = Spring.StiffnessHigh
        ),
        label = "optionScale"
    )

    Surface(
        shape = CircleShape,
        color = if (showHovered)
            MaterialTheme.colorScheme.primaryContainer
        else
            MaterialTheme.colorScheme.secondaryContainer.copy(alpha = alpha),
        tonalElevation = if (showHovered) 8.dp else 2.dp,
        modifier = Modifier
            .offset {
                IntOffset(
                    x = offsetX.roundToInt(),
                    y = offsetY.roundToInt()
                )
            }
            .scale(animatedScale)
            .size(OPTION_SIZE_DP.dp)
            .border(
                width = if (isActive) 2.dp else 1.dp,
                color = if (isActive) MaterialTheme.colorScheme.primary
                        else MaterialTheme.colorScheme.onSecondaryContainer,
                shape = CircleShape
            )
            .hoverable(interactionSource = interactionSource)
            .pointerInput(Unit) {
                detectTapGestures(onTap = { onClick() })
            }
    ) {
        Box(
            modifier = Modifier
                .size(OPTION_SIZE_DP.dp)
                .background(
                    color = if (showHovered)
                        MaterialTheme.colorScheme.primaryContainer
                    else
                        MaterialTheme.colorScheme.secondaryContainer.copy(alpha = alpha),
                    shape = CircleShape
                )
                .padding(4.dp),
            contentAlignment = Alignment.Center
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = if (showHovered)
                    MaterialTheme.colorScheme.onPrimaryContainer
                else
                    MaterialTheme.colorScheme.onSecondaryContainer.copy(alpha = alpha),
                textAlign = TextAlign.Center,
                maxLines = 1
            )
        }
    }
}